import { useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import clsx from 'clsx';

export type PageTab = {
  /** Value written to `?tab=` — keep stable, old links depend on it. */
  id: string;
  label: string;
  icon?: React.ReactNode;
  render: () => React.ReactNode;
};

type Props = {
  title: string;
  breadcrumb?: string;
  description?: string;
  /** Topbar actions that apply to every tab. Per-tab actions belong in the tab. */
  right?: React.ReactNode;
  tabs: PageTab[];
};

/**
 * One page hosting several sub-pages as tabs, with the active tab in the URL so
 * deep links, refresh and the back button all behave. Sub-pages take an
 * `embedded` prop and skip their own Topbar; this shell owns the page header.
 *
 * Use where the sidebar had multiple entries for different views of the SAME
 * thing. Genuinely separate jobs should stay separate pages.
 */
export function TabbedPage({ title, breadcrumb, description, right, tabs }: Props) {
  const [params, setParams] = useSearchParams();

  const raw = params.get('tab');
  const active = tabs.find((t) => t.id === raw) ?? tabs[0];

  const selectTab = (id: string) => {
    // Replace rather than merge: a filter from the previous tab rarely applies
    // to the next one, and stale params confuse the sub-page that reads them.
    const next = new URLSearchParams();
    next.set('tab', id);
    setParams(next);
  };

  return (
    <>
      <Topbar title={title} breadcrumb={breadcrumb} description={description} right={right} />

      <div className="border-b border-border px-7">
        <div className="flex items-center gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              className={clsx(
                'inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                active.id === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {active.render()}
    </>
  );
}
